import type { TOC } from '@ember/component/template-only';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelNetRevenueCostDrilldownSection from 'lumen/components/hotel/net-revenue/cost-drilldown-section';
import HotelNetRevenueCostDrilldownSectionTableLayout from 'lumen/components/hotel/net-revenue/cost-drilldown-section/table-layout';
import HotelNetRevenueDatepickersComparisonPicker from 'lumen/components/hotel/net-revenue/datepickers/comparison-picker';
import HotelNetRevenueKpiSection from 'lumen/components/hotel/net-revenue/kpi-section';
import HotelNetRevenueSectionHeader from 'lumen/components/hotel/net-revenue/section-header';
import HotelNetRevenueStayDateChart from 'lumen/components/hotel/net-revenue/stay-date-chart';
import type HotelNetRevenueController from 'lumen/controllers/hotel/net-revenue';
import type { NetRevenueModel } from 'lumen/routes/hotel/net-revenue';

interface HotelNetRevenueSignature {
  Args: {
    controller: HotelNetRevenueController;
    model: NetRevenueModel;
  };
}

<template>
  <GeneralPageHeader>
    <div
      class="content-wrapper flex items-center"
      data-test-id="net-revenue-date-picker"
    >
      <div class="flex items-center gap-400">
        {{! @glint-expect-error: Incorrect type }}
        <GeneralDatepickersDatepickerWithSidebar
          @maxRange="2 years"
          @range={{@controller.range}}
          @rangeChanged={{@controller.rangeChanged}}
          @sidebarLists={{@controller.datePickerRangeOptions}}
        />
        <HotelNetRevenueDatepickersComparisonPicker
          @maxDate={{@controller.datePickerComparisonMaxDate}}
          @maxRange="2 years"
          @minDate={{null}}
          @numberOfDays={{@controller.comparisonLengthInDays}}
          @range={{@controller.comparisonRange}}
          @rangeChanged={{@controller.comparisonRangeChanged}}
          @selectedOptionId={{@controller.comparisonMode}}
          @sidebarLists={{@controller.comparisonDatePickerRangeOptions}}
        />
      </div>
    </div>
  </GeneralPageHeader>
  <GeneralPageMain>
    <div
      class="content-wrapper flex flex-col gap-800 pt-800 pb-1000"
      data-test-id="net-revenue-page"
    >
      <div class="flex flex-col gap-400">
        <HotelNetRevenueSectionHeader
          @comparisonLabel={{@controller.comparisonLabel}}
          @isLoading={{@controller.isLoading}}
          @title={{t "net_revenue.kpi_section_title"}}
        />
        <HotelNetRevenueKpiSection
          @isLoading={{@controller.isLoading}}
          @kpiData={{@controller.kpiData}}
        />
      </div>
      <div class="flex flex-col gap-400">
        <HotelNetRevenueSectionHeader
          @comparisonLabel={{@controller.comparisonLabel}}
          @isLoading={{@controller.isLoading}}
          @title={{t "net_revenue.stay_date_chart_title"}}
        >
          <:right>
            <div
              class="flex items-center gap-200"
              data-test-id="stay-date-chart-view-by"
            >
              <span class="text-300--regular">{{t "general.view_by"}}</span>
              <PrismSelectInput
                @onChange={{@controller.stayDateGranularityChanged}}
                @options={{@controller.viewByOptions}}
                @value={{@controller.currentViewByOption}}
              />
            </div>
          </:right>
        </HotelNetRevenueSectionHeader>
        <HotelNetRevenueStayDateChart
          @currencyCode={{@controller.currencyCode}}
          @granularity={{@controller.effectiveStayDateGranularity}}
          @isError={{@controller.isError}}
          @isLoading={{@controller.isLoading}}
          @stayDateData={{@controller.stayDateData}}
        />
      </div>
      <div class="flex flex-col gap-400">
        <HotelNetRevenueSectionHeader
          @comparisonLabel={{@controller.comparisonLabel}}
          @isLoading={{@controller.isLoading}}
          @title={{t "net_revenue.cost_drilldown_title"}}
        >
          <:right>
            <HotelNetRevenueCostDrilldownSectionTableLayout
              @columnOrder={{@controller.costTableColumnOrder}}
              @hiddenColumns={{@controller.costTableHiddenColumns}}
              @onConfirm={{@controller.costTableLayoutChanged}}
            />
          </:right>
        </HotelNetRevenueSectionHeader>
        <HotelNetRevenueCostDrilldownSection
          @columnOrder={{@controller.costTableColumnOrder}}
          @data={{@controller.costProfileData}}
          @hiddenColumns={{@controller.costTableHiddenColumns}}
          @isError={{@controller.isError}}
          @isLoading={{@controller.isLoading}}
        />
      </div>
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelNetRevenueSignature>;
